import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getSystemSettings,
  updateSystemSettings,
} from "../../../api/services/api";
import Loader from "../../../components/Loader";
import { RoleGuard } from "../../../components/RBAC";

export const SystemSettings = () => {
  const queryClient = useQueryClient();
  const [platformCommission, setPlatformCommission] = useState("");
  const [vatPercentage, setVatPercentage] = useState("");
  const [minSessionPrice, setMinSessionPrice] = useState("");
  const [maxSessionPrice, setMaxSessionPrice] = useState("");
  const [allowNewRegistrations, setAllowNewRegistrations] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [autoApproveClinicans, setAutoApproveClinicans] = useState(false);
  const [sessionCancellationHours, setSessionCancellationHours] = useState("");
  const [payoutProcessingDays, setPayoutProcessingDays] = useState("");

  const { data, isLoading } = useQuery("system-settings", getSystemSettings, {
    onSuccess: (res: any) => {
      const settings = res?.data || {};
      setPlatformCommission(settings.platformCommission || "");
      setVatPercentage(settings.vatPercentage ?? "15");
      setMinSessionPrice(settings.minSessionPrice || "");
      setMaxSessionPrice(settings.maxSessionPrice || "");
      setAllowNewRegistrations(settings.allowNewRegistrations !== false);
      setMaintenanceMode(settings.maintenanceMode || false);
      setAutoApproveClinicans(settings.autoApproveClinicans || false);
      setSessionCancellationHours(settings.sessionCancellationHours || "");
      setPayoutProcessingDays(settings.payoutProcessingDays || "");
    },
  });

  const { mutate: updateSettingsMutate, isLoading: isUpdating } = useMutation(
    updateSystemSettings,
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("system-settings");
        toast.success(res?.data?.message || "Settings updated successfully");
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to update settings"
        );
      },
    }
  );

  const handleSave = () => {
    const settings = {
      platformCommission: parseFloat(platformCommission),
      vatPercentage: parseFloat(vatPercentage),
      minSessionPrice: parseFloat(minSessionPrice),
      maxSessionPrice: parseFloat(maxSessionPrice),
      allowNewRegistrations,
      maintenanceMode,
      autoApproveClinicans,
      sessionCancellationHours: parseInt(sessionCancellationHours),
      payoutProcessingDays: parseInt(payoutProcessingDays),
    };

    updateSettingsMutate(settings);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box my={2}>
      <Typography variant="h3" mb={3}>
        System Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Financial Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Financial Settings
              </Typography>
              <TextField
                fullWidth
                label="Platform Commission (%)"
                variant="outlined"
                value={platformCommission}
                onChange={(e) => setPlatformCommission(e.target.value)}
                type="number"
                sx={{ mb: 2 }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
              <TextField
                fullWidth
                label="VAT (%)"
                variant="outlined"
                value={vatPercentage}
                onChange={(e) => setVatPercentage(e.target.value)}
                type="number"
                sx={{ mb: 2 }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Value-added tax applied to session payments (Saudi standard: 15%)"
              />
              <TextField
                fullWidth
                label="Minimum Session Price (SAR)"
                variant="outlined"
                value={minSessionPrice}
                onChange={(e) => setMinSessionPrice(e.target.value)}
                type="number"
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                fullWidth
                label="Maximum Session Price (SAR)"
                variant="outlined"
                value={maxSessionPrice}
                onChange={(e) => setMaxSessionPrice(e.target.value)}
                type="number"
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                fullWidth
                label="Payout Processing Days"
                variant="outlined"
                value={payoutProcessingDays}
                onChange={(e) => setPayoutProcessingDays(e.target.value)}
                type="number"
                inputProps={{ min: 1, max: 30 }}
                helperText="Number of days to process clinician payouts"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Session Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Session Settings
              </Typography>
              <TextField
                fullWidth
                label="Session Cancellation Hours"
                variant="outlined"
                value={sessionCancellationHours}
                onChange={(e) => setSessionCancellationHours(e.target.value)}
                type="number"
                inputProps={{ min: 1, max: 72 }}
                helperText="Hours before session start time to allow cancellation"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* System Controls */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                System Controls
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowNewRegistrations}
                    onChange={(e) => setAllowNewRegistrations(e.target.checked)}
                  />
                }
                label="Allow New User Registrations"
              />
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={maintenanceMode}
                    onChange={(e) => setMaintenanceMode(e.target.checked)}
                  />
                }
                label="Maintenance Mode"
              />
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                When enabled, only admins can access the system
              </Typography>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={autoApproveClinicans}
                    onChange={(e) => setAutoApproveClinicans(e.target.checked)}
                  />
                }
                label="Auto-Approve Clinicians"
              />
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                Automatically approve new clinician registrations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save Settings"}
            </Button>
          </RoleGuard>
        </Grid>
      </Grid>
    </Box>
  );
};
